import type { ReactNode } from 'react';
import type { ObjectColor, ObjectIcon } from '@dataroom/core';
import { Icon } from '../icons';
import { useUiT } from '../i18n';
import { ACCENT_HEX, objectColors, objectIcons } from '../tokens';
import { cx } from './cx';

export type AvatarProps = {
  /** Initials (e.g. "MK"); empty renders the dashed "unassigned" avatar. */
  initials?: string | null;
  color?: string;
  size?: number;
  name?: string;
  className?: string;
};

const linear = (c: number) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};
const luminance = ([r, g, b]: number[]) => 0.2126 * linear(r!) + 0.7152 * linear(g!) + 0.0722 * linear(b!);

/**
 * The member colour, darkened just enough for white initials to reach 4.5:1 (WCAG AA): light hues
 * (amber, sky, mint) keep their identity but stay readable. Non-hex colours pass through.
 */
export function avatarBackground(color: string): string {
  const m = /^#([0-9a-f]{6})$/i.exec(color.trim());
  if (!m) return color;
  let rgb = [0, 2, 4].map((i) => parseInt(m[1]!.slice(i, i + 2), 16));
  if (1.05 / (luminance(rgb) + 0.05) >= 4.5) return color;
  for (let step = 0; step < 24 && 1.05 / (luminance(rgb) + 0.05) < 4.5; step++)
    rgb = rgb.map((c) => c * 0.94);
  return `#${rgb.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;
}

/** Member avatar: colored circle with white initials. */
export function Avatar({ initials, color = ACCENT_HEX, size = 26, name, className }: AvatarProps) {
  const t = useUiT();
  if (!initials) {
    return (
      <span
        className={cx('av', 'av-empty', className)}
        style={{ width: size, height: size }}
        aria-label={name ?? t('ui.unassigned')}
        role="img"
      />
    );
  }
  return (
    <span
      className={cx('av', className)}
      style={{
        width: size,
        height: size,
        background: avatarBackground(color),
        fontSize: Math.max(9, Math.round(size * 0.38)),
      }}
      title={name}
      role="img"
      aria-label={name ?? initials}
    >
      {initials}
    </span>
  );
}

export function AvatarGroup({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cx('av-group', className)}>{children}</span>;
}

export type ObjectTileProps = {
  color: ObjectColor;
  icon: ObjectIcon;
  size?: number;
  className?: string;
  label?: string;
};

/** Object identity tile (tinted square with the object's icon). */
export function ObjectTile({ color, icon, size = 28, className, label }: ObjectTileProps) {
  const c = objectColors[color] ?? objectColors.slate;
  return (
    <span
      className={cx('tile', className)}
      style={{ width: size, height: size, background: c.bg, color: c.fg, borderRadius: size >= 28 ? 8 : 6 }}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
    >
      <Icon name={objectIcons[icon] ?? 'box'} size={Math.round(size * 0.57)} />
    </span>
  );
}

/** Small letter tile used for record chips (company initial on deal cards). */
export function LetterTile({
  letter,
  color,
  size = 20,
}: {
  letter: string;
  color: ObjectColor;
  size?: number;
}) {
  const c = objectColors[color] ?? objectColors.slate;
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: 5,
        background: c.bg,
        color: c.fg,
        fontSize: size * 0.52,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {letter.charAt(0).toUpperCase()}
    </span>
  );
}
